/** 第三方登录回跳中必须从地址栏和埋点删除的认证参数。 */
export const THIRD_PARTY_AUTH_QUERY_PARAMS = [
  'token',
  'store_id',
  'isRegister',
  'registerType',
  'eventType',
  'third_login_msg',
] as const;

/** beforeInteractive URL guard 的固定 Script id。 */
export const THIRD_PARTY_LOGIN_URL_GUARD_SCRIPT_ID = 'zeelool-third-party-login-url-guard';

/** window 内存中临时快照的固定属性名。 */
export const THIRD_PARTY_LOGIN_URL_SNAPSHOT_KEY = '__ZEELOOL_THIRD_PARTY_LOGIN_URL_SNAPSHOT__';

/** App WebView 在 userAgent 中使用的稳定标记。 */
const APP_WEBVIEW_USER_AGENT_MARKER = 'nextmar-xms-app';

/** 第三方登录回跳参数快照。 */
export type ThirdPartyLoginParams = Partial<
  Record<(typeof THIRD_PARTY_AUTH_QUERY_PARAMS)[number], string>
>;

/** 第三方登录脱敏后的埋点 URL 与站内导航路径。 */
export interface ThirdPartyLoginUrlInfo {
  trackingUrl: string;
  navigationPath: string;
}

/** beforeInteractive guard 传递给 Hook 的单次内存快照。 */
export interface ThirdPartyLoginUrlSnapshot {
  params: ThirdPartyLoginParams;
  urlInfo: ThirdPartyLoginUrlInfo;
}

/** guard 执行所需的最小浏览器能力。 */
type ThirdPartyLoginGuardTarget = Pick<Window, 'history' | 'location' | 'navigator'>;

/**
 * 执行无外部引用的 URL guard。
 * 该函数会被序列化为 inline Script，因此函数体内不得捕获模块变量。
 */
function executeThirdPartyLoginUrlGuard(
  target: ThirdPartyLoginGuardTarget,
  snapshotKey: string,
  authQueryParams: readonly string[],
  appUserAgentMarker: string,
): void {
  if (target.navigator.userAgent.toLowerCase().includes(appUserAgentMarker)) return;
  if (Object.prototype.hasOwnProperty.call(target, snapshotKey)) return;

  const url = new URL(target.location.href);
  const params: Record<string, string> = {};
  authQueryParams.forEach((param) => {
    const value = url.searchParams.get(param);
    if (value !== null) params[param] = value;
  });
  if (!params.token && !params.third_login_msg) return;

  authQueryParams.forEach((param) => url.searchParams.delete(param));
  const urlInfo = Object.freeze({
    trackingUrl: url.toString(),
    navigationPath: `${url.pathname}${url.search}${url.hash}`,
  });
  const snapshot = Object.freeze({
    params: Object.freeze(params),
    urlInfo,
  });

  target.history.replaceState(target.history.state, '', urlInfo.navigationPath);
  Object.defineProperty(target, snapshotKey, {
    configurable: true,
    enumerable: false,
    writable: false,
    value: snapshot,
  });
}

/** 真实浏览器和单测共用的 URL guard 执行入口。 */
export function guardThirdPartyLoginUrl(target: Window = window): void {
  executeThirdPartyLoginUrlGuard(
    target,
    THIRD_PARTY_LOGIN_URL_SNAPSHOT_KEY,
    THIRD_PARTY_AUTH_QUERY_PARAMS,
    APP_WEBVIEW_USER_AGENT_MARKER,
  );
}

/**
 * beforeInteractive Script 的自包含源码。
 * 只传入 JSON 序列化常量，不依赖打包器模块作用域。
 */
export const THIRD_PARTY_LOGIN_URL_GUARD_SOURCE = `(${executeThirdPartyLoginUrlGuard.toString()})(window,${JSON.stringify(
  THIRD_PARTY_LOGIN_URL_SNAPSHOT_KEY,
)},${JSON.stringify(THIRD_PARTY_AUTH_QUERY_PARAMS)},${JSON.stringify(
  APP_WEBVIEW_USER_AGENT_MARKER,
)});`;

/** 判断 window 属性是否为 guard 产生的有效快照。 */
function isThirdPartyLoginUrlSnapshot(value: unknown): value is ThirdPartyLoginUrlSnapshot {
  if (!value || typeof value !== 'object') return false;
  const snapshot = value as Partial<ThirdPartyLoginUrlSnapshot>;
  if (!snapshot.params || typeof snapshot.params !== 'object') return false;
  if (!snapshot.urlInfo || typeof snapshot.urlInfo !== 'object') return false;
  return (
    typeof snapshot.urlInfo.trackingUrl === 'string' &&
    typeof snapshot.urlInfo.navigationPath === 'string'
  );
}

/** 读取并立即删除单次第三方登录回跳快照。 */
export function consumeThirdPartyLoginUrlSnapshot(
  target: Window = window,
): ThirdPartyLoginUrlSnapshot | null {
  const targetRecord = target as unknown as Record<string, unknown>;
  const snapshot = targetRecord[THIRD_PARTY_LOGIN_URL_SNAPSHOT_KEY];
  if (!Reflect.deleteProperty(target, THIRD_PARTY_LOGIN_URL_SNAPSHOT_KEY)) return null;
  return isThirdPartyLoginUrlSnapshot(snapshot) ? snapshot : null;
}

/** 删除第三方认证参数，同时生成埋点完整 URL 和路由相对路径。 */
export function sanitizeThirdPartyLoginUrl(href: string): ThirdPartyLoginUrlInfo {
  const url = new URL(href);
  THIRD_PARTY_AUTH_QUERY_PARAMS.forEach((param) => url.searchParams.delete(param));
  return {
    trackingUrl: url.toString(),
    navigationPath: `${url.pathname}${url.search}${url.hash}`,
  };
}
